/**
 * PostHog Events Module
 * Tracks funnel events directly via posthog.capture()
 *
 * Events tracked:
 * - product_added_to_cart: Product added to cart (art print + readymade frame)
 * - customization_started: SKU picker modal opened
 * - customization_completed: SKU picker selection confirmed
 * - style_preset_selected: User picks a preset style card
 * - style_sheet_opened: User opens All Styles / Custom Style sheet
 * - style_sheet_tab_switched: User switches between All Styles ↔ Custom Style
 * - style_custom_applied: User applies a custom configuration
 * - size_changed: User changes size (art print + readymade frame)
 * - unit_toggled: User switches cm/in unit (both PDPs)
 * - frame_color_changed: User picks a frame colour (readymade frame)
 * - glazing_changed: User picks a glazing option (readymade frame)
 * - border_changed: User picks a border/mount option (readymade frame)
 * - photo_uploaded: User uploads a photo (readymade frame)
 * - advisor_opened: User opens the Art Advisor (both PDPs)
 * - room_background_selected: User picks a room background
 * - wall_color_selected: User picks a wall color
 * - about_style_viewed: User opens the "About this style" dialog
 * - added_to_wishlist: Product liked via in-house wishlist
 * - removed_from_wishlist: Product unliked via in-house wishlist
 * - klaviyo_email_captured: Klaviyo popup signup completed
 * - b2b_form_submitted: B2B enquiry form on art-for-business page
 *
 * Post-click UX instrumentation (2026-07):
 * - collection_product_clicked: product card click on a collection page, with grid
 *   position and section (featured hero_products zone vs main grid)
 * - gallery_interacted: PDP media gallery interaction (slide change / view-mode
 *   pill / zoom) — zoom/view_mode deduped per pageview; slide_changed deduped
 *   per SLIDE identity (slide_role/slide_kind props) so slide reach is
 *   measurable, capped at GALLERY_MAX_EVENTS per pageview
 * - configurator_seen: size/frame configurator entered the viewport on a PDP —
 *   25% session sample (impression denominator for "saw vs used")
 * - size_sheet_opened / size_sheet_applied: the "View all N sizes" sheet —
 *   open + choose-from-sheet (distinct from inline size_changed taps)
 * - rage_click: 3+ pointerdowns within 800ms in a 30px radius — capped 2/pageview
 * - product_added_to_cart also carries the configured state (size/framing/border
 *   URL params) + whether it changed since landing
 */

(function () {
  // Ensure PostHog is available
  const getPostHog = () => window.posthog;

  const isPostHogReady = () => {
    const ph = getPostHog();
    return ph && typeof ph.capture === 'function';
  };

  // PDP variant — always 'new' now that the new PDP is graduated.
  const getPdpVariant = () => 'new';

  // PDP type — 'art_print' or 'readymade_frame' based on page template
  const getPdpType = () =>
    document.getElementById('RMFrameProductData') ? 'readymade_frame' : 'art_print';

  // Product ID from the DOM (PDP pages)
  const getProductId = () => {
    const el = document.querySelector('[data-product-id]');
    return el?.dataset.productId || null;
  };

  // Configured state from the PDP URL params (the configurator maintains these
  // via history.replaceState — see pdp-surgical-config.js / readymade-frame-pdp.js).
  const CONFIG_PARAMS = ['size', 'framing', 'border', 'frame-color', 'glazing'];
  const getConfigState = () => {
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, string | null>} */
    const out = {};
    for (const key of CONFIG_PARAMS) out[key] = params.get(key);
    return out;
  };
  const initialConfigState = JSON.stringify(getConfigState());

  // ---------- ADD TO CART ----------
  document.addEventListener('cart:update', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    const data = detail.data || {};

    // Skip if this was an error
    if (data.didError) return;

    // Only track actual additions (not removals/updates)
    const validSources = ['product-form-component', 'bundle-submission', 'readymade-frame-pdp'];
    if (!validSources.includes(data.source)) return;

    /** @type {Record<string, any>} */
    const properties = {
      product_id: data.productId || null,
      variant_id: data.variantId || null,
      quantity: data.itemCount || 1,
      source: data.source,
      cart_value_cents: data.finalLinePrice ?? null,
      currency: data.currency || null,
      pdp_variant: getPdpVariant(),
    };

    // Price from Shopify cart API is in cents — convert to decimal
    if (data.price != null) {
      properties.value = data.price / 100;
    }
    if (data.currency) {
      properties.currency = data.currency;
    }

    // Configured state at the moment of the add (which size tier / framing sells,
    // and whether the shopper configured at all before adding).
    const config = getConfigState();
    properties.size_handle = config.size;
    properties.framing = config.framing;
    properties.border = config.border;
    properties.frame_color = config['frame-color'];
    properties.config_changed = JSON.stringify(config) !== initialConfigState;

    getPostHog().capture('product_added_to_cart', properties);
  });

  // ---------- CUSTOMIZATION STARTED (SKU picker opened) ----------
  document.addEventListener('sku-picker:modal-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('customization_started', {
      option_type: detail.optionType || null,
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- CUSTOMIZATION COMPLETED (SKU picker selection confirmed) ----------
  document.addEventListener('sku-picker:selection-confirmed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('customization_completed', {
      option_type: detail.optionType || null,
      product_id: detail.productId || null,
      variant_id: detail.variantId || null,
      selection: detail.selection || null,
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE PRESET SELECTED ----------
  document.addEventListener('style-picker:preset-selected', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_preset_selected', {
      preset_id: detail.presetId || null,
      preset_name: detail.presetName || null,
      framing: detail.framing || null,
      frame_color: detail.frameColor || null,
      border: detail.border || null,
      source: detail.source || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE SHEET OPENED ----------
  document.addEventListener('style-picker:sheet-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_sheet_opened', {
      initial_tab: detail.initialTab || null,
      // 'surgical-frames-drawer' when the surgical PDP's "View all N frames"
      // drawer dispatched it (2026-07); absent/null = legacy style-picker sheet.
      surface: detail.surface || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- SIZE SHEET OPENED / APPLIED ----------
  // The surgical PDP's "View all N sizes" sheet (true-to-scale room mock).
  // Opened = engagement with the long-tail size range; applied = a size chosen
  // FROM the sheet (inline picker taps fire size_changed with no sheet signal).
  document.addEventListener('pdp:size-sheet-opened', (e) => {
    if (!isPostHogReady()) return;
    getPostHog().capture('size_sheet_opened', {
      selected_size: e.detail?.selected || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  document.addEventListener('pdp:size-sheet-applied', (e) => {
    if (!isPostHogReady()) return;
    getPostHog().capture('size_sheet_applied', {
      size_handle: e.detail?.handle || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE SHEET TAB SWITCHED ----------
  document.addEventListener('style-picker:tab-switched', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_sheet_tab_switched', {
      from_tab: detail.fromTab || null,
      to_tab: detail.toTab || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE CUSTOM APPLIED ----------
  document.addEventListener('style-picker:custom-applied', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_custom_applied', {
      variant_id: detail.variantId || null,
      framing: detail.framing || null,
      frame_color: detail.frameColor || null,
      border: detail.border || null,
      product_type: detail.productType || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- SIZE CHANGED (art print PDP) ----------
  document.addEventListener('size-impact:changed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('size_changed', {
      size_handle: detail.handle || null,
      product_id: getProductId(),
      pdp_type: 'art_print',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- SIZE CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:size-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('size_changed', {
      size_handle: detail.handle || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- FRAME COLOUR CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:color-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('frame_color_changed', {
      color_handle: detail.handle || null,
      color_name: detail.colorName || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- GLAZING CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:glazing-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('glazing_changed', {
      glazing_handle: detail.handle || null,
      glazing_name: detail.glazingName || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- BORDER/MOUNT CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:border-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('border_changed', {
      border_handle: detail.handle || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- PHOTO UPLOADED (readymade frame PDP) ----------
  document.addEventListener('rmf:photo-uploaded', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('photo_uploaded', {
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- UNIT TOGGLED (both PDPs) ----------
  document.addEventListener('sku-picker:unit-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('unit_toggled', {
      unit: detail.unit || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- ADVISOR OPENED (both PDPs) ----------
  document.addEventListener('whisper:open-chat', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('advisor_opened', {
      intent: detail.intent || null,
      product_handle: detail.productHandle || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- ROOM BACKGROUND SELECTED ----------
  document.addEventListener('room-background:changed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    // Skip automatic room switches (e.g. triggered by size change)
    if (detail.isAutoSwitch) return;

    getPostHog().capture('room_background_selected', {
      room_index: detail.index ?? null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- WALL COLOR SELECTED ----------
  document.addEventListener('wall-color-change', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('wall_color_selected', {
      wall_color_hex: detail.hex || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- KLAVIYO SIGNUP COMPLETED ----------
  // Klaviyo multi-step forms dispatch per step. We debounce by form_id
  // with a 30s window (enough time to complete a phone step), and flush
  // immediately on page unload so we don't lose the event if they navigate.
  const klaviyoPending = {}; // form_id → { timeout, props }

  function flushKlaviyoEvents() {
    if (!isPostHogReady()) return;
    for (const formId of Object.keys(klaviyoPending)) {
      getPostHog().capture('klaviyo_email_captured', klaviyoPending[formId].props);
      delete klaviyoPending[formId];
    }
  }

  document.addEventListener('klaviyo:signup-completed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    const formId = detail.form_id || 'unknown';
    const props = {
      source: detail.source || 'popup',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      form_id: formId,
    };

    if (klaviyoPending[formId]) clearTimeout(klaviyoPending[formId].timeout);
    klaviyoPending[formId] = {
      props: props,
      timeout: setTimeout(() => {
        delete klaviyoPending[formId];
        getPostHog().capture('klaviyo_email_captured', props);
      }, 30000),
    };
  });

  window.addEventListener('beforeunload', flushKlaviyoEvents);

  // ---------- ATTENTIVE SIGNUP COMPLETED ----------
  document.addEventListener('attentive:signup-completed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    getPostHog().capture('attentive_email_captured', {
      source: detail.source || 'popup',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      form_id: detail.form_id || 'unknown',
    });
  });

  // ---------- B2B FORM SUBMITTED ----------
  document.addEventListener('b2b:form-submitted', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('b2b_form_submitted', {
      source: detail.source || 'b2b_form',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      has_company: !!detail.company,
      form_id: detail.form_id || 'b2b-art-for-business',
    });
  });

  // ---------- ABOUT STYLE VIEWED ----------
  document.addEventListener('style-picker:about-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('about_style_viewed', {
      preset_id: detail.presetId || null,
      preset_name: detail.presetName || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- WISHLIST LIKE / UNLIKE ----------
  function handleWishlistEvent(e) {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    if (detail.event === 'added_to_wishlist' || detail.event === 'removed_from_wishlist') {
      getPostHog().capture(detail.event, {
        product_handle: detail.product_handle || null,
        product_id: detail.product_id || null,
        source: detail.source || null,
      });
    }
  }
  // Listen for both: 'wishlist:analytics' is canonical, 'custom-analytics' is
  // the old name still served from CDN cache until wishlist-state.js cache expires
  document.addEventListener('wishlist:analytics', handleWishlistEvent);
  document.addEventListener('custom-analytics', handleWishlistEvent);

  // ---------- VIEW SIMILAR CLICKED ----------
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.view-similar--v2, [data-click-event="tapped-view-similar"]');
    if (!btn || !isPostHogReady()) return;
    var handle = window.location.pathname.split('/products/')[1];
    if (handle) handle = handle.split('?')[0];
    getPostHog().capture('view_similar_clicked', {
      product_handle: handle || '',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- NAV MENU OPENED ----------
  document.addEventListener('nav:menu-opened', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('nav_menu_opened', {
      menu: detail.menu || null,
      menu_label: detail.label || null,
      device: detail.device || null,
    });
  });

  // ---------- NAV LINK CLICKED ----------
  document.addEventListener('nav:link-clicked', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('nav_link_clicked', {
      menu: detail.menu || null,
      device: detail.device || null,
      url: detail.url || null,
      link_label: detail.label || null,
    });
  });

  // ============================================================
  //  CART & CHECKOUT FUNNEL
  // ============================================================

  // ---------- CART DRAWER OPENED ----------
  document.addEventListener('cart:drawer-opened', () => {
    if (!isPostHogReady()) return;
    getPostHog().capture('cart_viewed', { source: 'drawer' });
  });

  // ---------- CART PAGE VIEWED ----------
  if (document.querySelector('.cart-form, [data-cart-page]') && /\/cart\b/.test(window.location.pathname)) {
    if (isPostHogReady()) {
      getPostHog().capture('cart_viewed', { source: 'page' });
    }
  }

  // ---------- CART QUANTITY CHANGED ----------
  document.addEventListener('cart:quantity-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('cart_quantity_changed', {
      line: detail.line ?? null,
      quantity: detail.quantity ?? null,
    });
  });

  // ---------- CART ITEM REMOVED ----------
  document.addEventListener('cart:item-removed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('cart_item_removed', {
      product_handle: detail.product_handle || null,
      product_id: detail.product_id || null,
    });
  });

  // ---------- CHECKOUT BUTTON CLICKED ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#checkout, [name="checkout"]');
    if (!btn || !isPostHogReady()) return;
    getPostHog().capture('checkout_started', {
      source: document.querySelector('cart-drawer-component dialog[open]') ? 'drawer' : 'page',
    });
  });

  // ============================================================
  //  COLLECTION BROWSING
  // ============================================================

  // ---------- COLLECTION FILTER / SORT CHANGED ----------
  document.addEventListener('filter:update', (e) => {
    if (!isPostHogReady()) return;
    const params = e.detail?.queryParams;
    if (!params) return;
    const filters = {};
    const sort = params.get('sort_by') || null;
    for (const [key, value] of params.entries()) {
      if (key !== 'sort_by' && key !== 'q') {
        filters[key] = value;
      }
    }
    getPostHog().capture('collection_filtered', {
      sort_by: sort,
      filters: Object.keys(filters).length > 0 ? filters : null,
      collection_url: window.location.pathname,
    });
  });

  // ---------- COLLECTION LOAD MORE ----------
  document.addEventListener('collection:page-loaded', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('collection_page_loaded', {
      page: detail.page ?? null,
      product_count: detail.count ?? null,
      collection: detail.collection || null,
    });
  });

  // ============================================================
  //  SEARCH
  // ============================================================

  // ---------- SEARCH SUBMITTED (autocomplete) ----------
  document.addEventListener('search:submitted', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_submitted', {
      query: detail.query || null,
      source: detail.source || null,
      position: detail.position ?? null,
    });
  });

  // ---------- SEARCH RESULTS LOADED ----------
  document.addEventListener('search:results-loaded', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_results_loaded', {
      query: detail.query || null,
      result_count: detail.result_count ?? null,
      page: detail.page ?? null,
      execution_time_ms: detail.execution_time_ms ?? null,
    });
  });

  // ---------- SEARCH RESULT CLICKED ----------
  document.addEventListener('search:result-clicked', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_result_clicked', {
      query: detail.query || null,
      product_id: detail.product_id || null,
      product_handle: detail.product_handle || null,
      position: detail.position ?? null,
      page: detail.page ?? null,
    });
  });

  // ---------- SEARCH ZERO RESULTS ----------
  document.addEventListener('search:zero-results', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_zero_results', {
      query: detail.query || null,
      suggested_query: detail.suggested_query || null,
      execution_time_ms: detail.execution_time_ms ?? null,
    });
  });

  // ---------- HOMEPAGE: ADVISOR HERO SUBMIT ----------
  document.addEventListener('home:advisor-submit', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('homepage_advisor_submitted', {
      query: detail.query || null,
      has_photo: detail.has_photo || false,
      has_url: detail.has_url || false,
    });
  });

  // ---------- HOMEPAGE: FLOATING ADVISOR CLICKED ----------
  document.addEventListener('home:floating-advisor-clicked', () => {
    if (!isPostHogReady()) return;
    getPostHog().capture('homepage_floating_advisor_clicked');
  });

  // ---------- HOMEPAGE: ROOM / CATEGORY TILE CLICKED ----------
  document.addEventListener('click', (e) => {
    if (!isPostHogReady()) return;

    // Room tile
    const roomTile = e.target.closest('.home-rooms-grid__cell a, .home-rooms-grid a');
    if (roomTile) {
      getPostHog().capture('homepage_tile_clicked', {
        tile_type: 'room',
        url: roomTile.getAttribute('href') || null,
        label: roomTile.textContent?.trim()?.substring(0, 60) || null,
      });
      return;
    }

    // Category tile
    const catTile = e.target.closest('.home-categories-grid__cell a, .home-categories-grid a');
    if (catTile) {
      getPostHog().capture('homepage_tile_clicked', {
        tile_type: 'category',
        url: catTile.getAttribute('href') || null,
        label: catTile.textContent?.trim()?.substring(0, 60) || null,
      });
      return;
    }

    // Product rail card
    const railCard = e.target.closest('.home-product-rail a.product-card-v2, .home-product-rail .pcv2-link');
    if (railCard) {
      const rail = railCard.closest('.home-product-rail');
      getPostHog().capture('homepage_product_clicked', {
        url: railCard.getAttribute('href') || null,
        rail_source: rail?.dataset.source || null,
      });
    }
  });

  // Style Quiz nav item click
  document.querySelectorAll('.style-quiz-nav-item a').forEach(function(link) {
    link.addEventListener('click', function() {
      if (!isPostHogReady()) return;
      getPostHog().capture('style_quiz_nav_clicked');
    });
  });

  // ============================================================
  //  POST-CLICK UX INSTRUMENTATION (2026-07)
  // ============================================================

  // ---------- COLLECTION PRODUCT CARD CLICKED ----------
  // Grid position + section (the ad-hero-products featured zone vs the main grid).
  // Collection pages only; homepage rails and search results have their own events.
  document.addEventListener('click', (e) => {
    if (!isPostHogReady()) return;
    if (!window.location.pathname.includes('/collections/')) return;
    if (!(e.target instanceof Element)) return;
    const card = e.target.closest('a.product-card-v2');
    if (!card) return;
    const grid = card.closest('.product-grid');
    if (!grid) return;
    // Everything below runs off the interaction path: the grid-wide
    // querySelectorAll + capture must not contribute to INP on card taps.
    setTimeout(() => {
      const item = card.closest('.product-grid__item');
      const items = grid.querySelectorAll('.product-grid__item');
      const position = item ? Array.prototype.indexOf.call(items, item) + 1 : null;
      const handleMatch = (card.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      getPostHog().capture('collection_product_clicked', {
        product_handle: handleMatch ? handleMatch[1] : null,
        section: card.closest('.ad-hero-products') ? 'featured' : 'grid',
        position: position || null,
        grid_size: items.length || null,
        collection_url: window.location.pathname,
      }, { transport: 'sendBeacon' });
    }, 0);
  });

  // ---------- PDP GALLERY INTERACTED ----------
  // zoom / view_mode are deduped to the FIRST occurrence per pageview.
  // slide_changed is deduped per SLIDE IDENTITY instead, so we can measure
  // which slides (moka hero / room mocks / static marketing slides) people
  // actually reach — the PDP iteration-3 education-slide readout needs it.
  // A hard cap bounds the worst case: 2 (zoom+view_mode) + the slide count.
  const GALLERY_ROOTS =
    'media-gallery, gallery-wall-media-gallery, ai-photos-media-gallery, photo-print-media-gallery';
  const GALLERY_MAX_EVENTS = 12;
  const galleryCaptured = new Set();

  // Resolve a slide element to a stable identity:
  //   slide_role — the gallery's own data-slide-role (moka-hero / frame-mock /
  //     room-mock / video / additional)
  //   slide_kind — refines 'additional' via the mock-image container:
  //     'static:<handle>' (marketing slides), 'scene:<handle>' (lifestyle
  //     mocks), or 'image:<index>' (plain product media — index-keyed so each
  //     distinct photo dedups separately; review note on PR #166)
  const gallerySlideIdentity = (slide, index) => {
    if (!(slide instanceof Element)) return { role: null, kind: null };
    const role = slide.getAttribute('data-slide-role');
    if (role && role !== 'additional') return { role, kind: role };
    const mock = slide.querySelector('.mock-image-container');
    if (!mock) {
      return { role, kind: 'image:' + (typeof index === 'number' ? index : 'x') };
    }
    const handle = mock.getAttribute('data-mock-handle') || 'unknown';
    const isStatic = mock.classList.contains('mock-image-container--static');
    return { role, kind: (isStatic ? 'static:' : 'scene:') + handle };
  };

  const captureGalleryInteraction = (interaction, value, extra) => {
    if (!isPostHogReady() || galleryCaptured.size >= GALLERY_MAX_EVENTS) return;
    const dedupKey = extra?.slide_kind
      ? interaction + ':' + extra.slide_kind
      : interaction;
    if (galleryCaptured.has(dedupKey)) return;
    galleryCaptured.add(dedupKey);
    getPostHog().capture('gallery_interacted', {
      interaction,
      value: value || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
      ...(extra || {}),
    });
  };

  document.addEventListener('slideshow:select', (e) => {
    const detail = e.detail || {};
    if (!detail.userInitiated) return;
    if (!(e.target instanceof Element) || !e.target.closest(GALLERY_ROOTS)) return;
    const identity = gallerySlideIdentity(detail.slide, detail.index);
    captureGalleryInteraction('slide_changed', detail.trigger || null, {
      slide_role: identity.role,
      slide_kind: identity.kind,
      slide_index: typeof detail.index === 'number' ? detail.index : null,
    });
  });

  document.addEventListener('zoom-media:selected', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest(GALLERY_ROOTS)) return;
    captureGalleryInteraction('zoom');
  });

  // Capture phase: the room-preview pill router stopPropagation()s bubble-phase
  // clicks on the pill container, so a bubble listener here would never fire.
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const pill = e.target.closest('.pdp-mode-tabs [data-pill-action], .pdp-mode-tab[data-pill-action]');
    if (!pill) return;
    const action = pill.dataset.pillAction || null;
    setTimeout(() => captureGalleryInteraction('view_mode', action), 0);
  }, true);

  // ---------- CONFIGURATOR SEEN (25% session sample) ----------
  // Impression denominator for "saw the configurator vs used it". Sampled to bound
  // volume (~25% of PDP views); analysis divides by sample_rate.
  (() => {
    const configRoot = document.querySelector('.pdp-surgical-config-root');
    if (!configRoot || !('IntersectionObserver' in window)) return;
    const CONFIG_SEEN_RATE = 0.25;
    let sampled = false;
    try {
      let flag = sessionStorage.getItem('fy_cfgseen_sample');
      if (flag === null) {
        flag = Math.random() < CONFIG_SEEN_RATE ? '1' : '0';
        sessionStorage.setItem('fy_cfgseen_sample', flag);
      }
      sampled = flag === '1';
    } catch (err) {
      return; // no sessionStorage → skip rather than over-sample
    }
    if (!sampled) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      if (!isPostHogReady()) return;
      getPostHog().capture('configurator_seen', {
        product_id: getProductId(),
        pdp_type: getPdpType(),
        pdp_variant: getPdpVariant(),
        sample_rate: CONFIG_SEEN_RATE,
      });
    }, { threshold: 0.4 });
    observer.observe(configRoot);
  })();

  // ---------- RAGE CLICK (all sessions, capped 2/pageview) ----------
  // 3+ pointerdowns within 800ms inside a 30px radius. Hand-rolled so we don't
  // need PostHog autocapture (kept off for cost).
  let recentClicks = [];
  let rageCaptured = 0;
  document.addEventListener('pointerdown', (e) => {
    if (rageCaptured >= 2) return;
    const now = performance.now();
    recentClicks = recentClicks.filter((c) => now - c.t < 800);
    recentClicks.push({ t: now, x: e.clientX, y: e.clientY });
    if (recentClicks.length < 3) return;
    const [first] = recentClicks.slice(-3);
    const clustered = recentClicks
      .slice(-3)
      .every((c) => Math.hypot(c.x - first.x, c.y - first.y) < 30);
    if (!clustered) return;
    recentClicks = [];
    rageCaptured++;
    if (!isPostHogReady()) return;
    const target = e.target;
    // element inspection + capture deferred off the interaction path (INP)
    setTimeout(() => {
      const el = target instanceof Element ? target.closest('a, button, [role="button"]') || target : null;
      getPostHog().capture('rage_click', {
        element: el ? el.tagName.toLowerCase() : null,
        element_class: el && typeof el.className === 'string'
          ? el.className.trim().split(/\s+/).slice(0, 2).join(' ') || null
          : null,
        element_text: el?.textContent?.trim()?.replace(/\s+/g, ' ')?.slice(0, 40) || null,
        path: window.location.pathname,
      }, { transport: 'sendBeacon' });
    }, 0);
  }, { capture: true, passive: true });
})();
